import type { RoundupDriverGroupEnum } from '@repo/contracts/marketplace/offers/enums';
import type { AllDrivers, DriverType } from '../../../model/types';
import { getDriverLayout, getEmptyNotRelevantPurchaseOrIntroAPR } from './index';

/**
 * Source for maximum potential column counts.
 * Mobile: Maximum potential drivers as set in ./config.ts
 * Desktop: Maximum drivers for a given row as set in ./config.ts
 */
const MAX_MOBILE_COLUMNS = 7;
const MAX_DESKTOP_COLUMNS = 4;

export type MobileColumnCount = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type DesktopColumnCount = 1 | 2 | 3 | 4;

export type DriverLayoutVariant = {
  mobileColumns: `${MobileColumnCount}`;
  desktopColumns: `${DesktopColumnCount}`;
};

export type DriverLayoutVariantMap = Partial<Record<keyof AllDrivers, DriverLayoutVariant>>;

function toMobileColumnCount(rowLength: number): MobileColumnCount {
  return Math.min(rowLength, MAX_MOBILE_COLUMNS) as MobileColumnCount;
}

function toDesktopColumnCount(rowLength: number): DesktopColumnCount {
  return Math.min(rowLength, MAX_DESKTOP_COLUMNS) as DesktopColumnCount;
}

/**
 * Maps row drivers to layout variant keys for a breakpoint.
 */
export type GetDeviceRowsProps = {
  allDrivers: AllDrivers;
  driverGroup: RoundupDriverGroupEnum;
  processedLayout?: ReturnType<typeof processDriverLayout>;
};

export const processDriverLayout = ({
  allDrivers,
  driverGroup,
}: {
  allDrivers: AllDrivers;
  driverGroup: RoundupDriverGroupEnum;
}) => {
  const driverLayout = getDriverLayout({
    driverGroup,
  });

  const emptyNotRelevantPurchaseOrIntroAPR = getEmptyNotRelevantPurchaseOrIntroAPR({
    allDrivers,
    secondRow: driverLayout.desktop.secondRow,
  });

  if (emptyNotRelevantPurchaseOrIntroAPR.length > 0) {
    emptyNotRelevantPurchaseOrIntroAPR.forEach((purchaseOrIntroAPR: string) => {
      driverLayout.desktop.secondRow = driverLayout.desktop.secondRow.filter(
        (driverKey: string) => driverKey !== purchaseOrIntroAPR,
      );
      driverLayout.drivers = driverLayout.drivers.filter(
        (driverKey: string) => driverKey !== purchaseOrIntroAPR,
      );
      driverLayout.mobile.rowWideDrivers = driverLayout.mobile.rowWideDrivers.filter(
        (driverKey: string) => driverKey !== purchaseOrIntroAPR,
      );
    });
  }

  const isRcsInFirstRow = driverLayout.desktop.firstRow.includes('recommendedCreditScore');
  const shouldMoveRecommendedCreditScoreToSecondRow =
    isRcsInFirstRow && emptyNotRelevantPurchaseOrIntroAPR.length === 2;

  if (shouldMoveRecommendedCreditScoreToSecondRow) {
    // biome-ignore format: lint suppression requires assertion on same line
    // biome-ignore lint/style/noNonNullAssertion: firstRow is non-empty when this branch is reached
    const lastDriverFromFirstRow = driverLayout.desktop.firstRow[driverLayout.desktop.firstRow.length - 1]!;

    driverLayout.desktop.firstRow = driverLayout.desktop.firstRow.filter(
      (driverKey: string) => driverKey !== 'recommendedCreditScore',
    );
    driverLayout.desktop.secondRow.push('recommendedCreditScore');

    driverLayout.drivers = driverLayout.drivers.filter(
      (driverKey: string) => driverKey !== 'recommendedCreditScore',
    );
    driverLayout.drivers.push('recommendedCreditScore');

    driverLayout.mobile.rowWideDrivers.push(lastDriverFromFirstRow);
  }

  return {
    driverLayout,
    emptyNotRelevantPurchaseOrIntroAPRCount: emptyNotRelevantPurchaseOrIntroAPR.length,
  };
};

export const getMobileRows = ({ allDrivers, driverGroup, processedLayout }: GetDeviceRowsProps) => {
  const { driverLayout } =
    processedLayout ??
    processDriverLayout({
      allDrivers,
      driverGroup,
    });

  const { drivers, mobile } = driverLayout;
  const { rowWideDrivers } = mobile;

  const rows = drivers.reduce((acc: DriverType[][], driverKey: string, currentIndex: number) => {
    const shouldTakeEntireRow = rowWideDrivers.includes(driverKey as never);
    const driver = allDrivers[driverKey as keyof AllDrivers];
    if (!driver) return acc;
    let lastRow = acc.length - 1;
    if (lastRow < 0) {
      acc[0] = [];
      lastRow = 0;
    }
    if (shouldTakeEntireRow) {
      acc.push([driver]);
      if (currentIndex !== drivers.length - 1) {
        acc.push([]);
      }
    } else {
      // biome-ignore lint/style/noNonNullAssertion: lastRow is always a valid index (initialized above)
      acc[lastRow]!.push(driver);
    }
    return acc;
  }, []);

  return rows;
};

export const getDesktopRows = ({
  allDrivers,
  driverGroup,
  processedLayout,
}: GetDeviceRowsProps) => {
  const { driverLayout } =
    processedLayout ??
    processDriverLayout({
      allDrivers,
      driverGroup,
    });

  const { firstRow, secondRow } = driverLayout.desktop;

  const hydratedFirstRow = firstRow.map(
    (driverKey: string) => allDrivers[driverKey as keyof AllDrivers],
  );
  const hydratedSecondRow = secondRow.map(
    (driverKey: string) => allDrivers[driverKey as keyof AllDrivers],
  );

  return { firstRow: hydratedFirstRow, secondRow: hydratedSecondRow };
};

function mapRowDriversToVariant(
  row: DriverType[],
  variantMap: DriverLayoutVariantMap,
  breakpoint: 'mobile' | 'desktop',
): void {
  if (row.length === 0) return;

  row.forEach(driver => {
    const driverId = driver.id as keyof AllDrivers;
    const existing = variantMap[driverId];

    if (breakpoint === 'mobile') {
      variantMap[driverId] = {
        mobileColumns: `${toMobileColumnCount(row.length)}`,
        desktopColumns: existing?.desktopColumns ?? '1',
      };
      return;
    }

    variantMap[driverId] = {
      mobileColumns: existing?.mobileColumns ?? '1',
      desktopColumns: `${toDesktopColumnCount(row.length)}`,
    };
  });
}

/**
 * Returns a mapping of driver ID to layout variant keys.
 * Use with `driverFlexColumns.raw` on the Driver `css` prop.
 *
 * @example
 * const layoutVariantMap = getDriverLayoutVariantMap({ allDrivers, driverGroup });
 * // Then in your map:
 * {Object.values(allDrivers).map(driver => (
 *   <Driver css={driverFlexColumns.raw(layoutVariantMap[driver.id]!)} />
 * ))}
 */
export const getDriverLayoutVariantMap = ({
  allDrivers,
  driverGroup,
  processedLayout: preProcessedLayout,
}: GetDeviceRowsProps): DriverLayoutVariantMap => {
  const processedLayout =
    preProcessedLayout ??
    processDriverLayout({
      allDrivers,
      driverGroup,
    });

  const mobileRows = getMobileRows({
    allDrivers,
    driverGroup,
    processedLayout,
  });
  const { firstRow, secondRow } = getDesktopRows({
    allDrivers,
    driverGroup,
    processedLayout,
  });

  const variantMap: DriverLayoutVariantMap = {};

  mobileRows.forEach((row: DriverType[]) => {
    mapRowDriversToVariant(row, variantMap, 'mobile');
  });

  mapRowDriversToVariant(firstRow, variantMap, 'desktop');
  mapRowDriversToVariant(secondRow, variantMap, 'desktop');

  return variantMap;
};
